import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { CreateWorkOrder, WorkOrderPriority, type WorkOrder } from '@syndiqia/maintenance/contracts';
import { errorMessage } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, validate, type FieldErrors } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAssets, useMaintenanceMutation, useVendors, useWorkOrders } from '../../maintenance-queries';
import { useSyndicate } from '../../session';
import { MaintenanceFrame, centsOf } from './MaintenanceNav';
import { UnfiledInvoices } from './Purchasing';

export function WorkOrdersPage() {
  const { t, date, money } = useI18n();
  const s = useSyndicate();
  const [all, setAll] = useState(false);
  const orders = useWorkOrders(all ? 'all' : 'open');
  const m = t.maintenance;

  return (
    <MaintenanceFrame actions={s.can('maintenance.manage') && <Disclosure label={m.newWorkOrder}>{(close) => <WorkOrderForm onDone={close} />}</Disclosure>}>
      <UnfiledInvoices />
      <label className="inline-field">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
        {m.showAll}
      </label>
      {orders.isPending && <Loading label={t.loading} />}
      {orders.isError && <Alert>{errorMessage(orders.error, t)}</Alert>}
      {orders.data?.length === 0 && <Empty>{m.noWorkOrders}</Empty>}
      {!!orders.data?.length && (
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{m.workOrderTitle}</th>
              <th>{m.status}</th>
              <th>{m.asset}</th>
              <th>{m.vendor}</th>
              <th>{m.scheduledOn}</th>
              <th className="num">{m.estimate}</th>
            </tr>
          </thead>
          <tbody>
            {orders.data.map((w) => (
              <tr key={w.id}>
                <td className="cell-title">
                  <Link to={`${s.base}/maintenance/work-orders/${w.id}`}>
                    <span className="muted">#{w.number}</span> {w.title}
                  </Link>
                  {w.priority === 'urgent' && <span className="badge badge-urgent">{m.urgent}</span>}
                </td>
                <td data-label={m.status}>
                  <span className={`badge badge-wo-${w.status}`}>{m.statuses[w.status]}</span>
                </td>
                <td data-label={m.asset}>{w.assetName ?? m.none}</td>
                <td data-label={m.vendor}>{w.vendorName ?? m.none}</td>
                <td data-label={m.scheduledOn}>{w.completedOn ? date(w.completedOn) : w.scheduledOn ? date(w.scheduledOn) : m.none}</td>
                <td data-label={m.estimate} className="num">
                  {w.actualCostCents != null ? money(w.actualCostCents) : w.estimateCents != null ? money(w.estimateCents) : m.none}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </MaintenanceFrame>
  );
}

/** Creation form, also used from a member request (prefilled, linked to it). */
export function WorkOrderForm({
  onDone,
  onCreated,
  initial,
}: {
  onDone: () => void;
  onCreated?: (order: WorkOrder) => void;
  initial?: { title: string; description: string; priority: 'normal' | 'urgent'; requestId: string; assetId?: string | null; vendorId?: string | null };
}) {
  const { t } = useI18n();
  const s = useSyndicate();
  const navigate = useNavigate();
  const m = t.maintenance;
  const assets = useAssets();
  const vendors = useVendors();
  const [values, setValues] = useState({
    title: initial?.title ?? '',
    description: initial?.description ?? '',
    priority: initial?.priority ?? 'normal',
    assetId: initial?.assetId ?? '',
    vendorId: initial?.vendorId ?? '',
    scheduledOn: '',
    estimate: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useMaintenanceMutation<CreateWorkOrder, WorkOrder>((api, base, input) => api(`${base}/work-orders`, { method: 'POST', body: input }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const { estimate, ...rest } = values;
    const result = validate(CreateWorkOrder, { ...rest, estimateCents: centsOf(estimate), requestId: initial?.requestId });
    setErrors(result.errors ? { ...result.errors, estimate: result.errors.estimateCents ?? '' } : {});
    if (result.data) create.mutate(result.data, { onSuccess: (w) => (onCreated?.(w), onDone(), navigate(`${s.base}/maintenance/work-orders/${w.id}`)) });
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={m.workOrderTitle} error={errors.title}>
          <input value={values.title} onChange={set('title')} autoFocus />
        </Field>
        <Field label={m.priority}>
          <select value={values.priority} onChange={set('priority')}>
            {WorkOrderPriority.options.map((p) => (
              <option key={p} value={p}>
                {p === 'urgent' ? m.urgent : m.normal}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={m.description} hint={t.common.optional} error={errors.description}>
        <textarea rows={3} value={values.description} onChange={set('description')} />
      </Field>
      <div className="row">
        <Field label={m.asset} hint={t.common.optional}>
          <select value={values.assetId} onChange={set('assetId')}>
            <option value="">{m.none}</option>
            {assets.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={m.vendor} hint={t.common.optional}>
          <select value={values.vendorId} onChange={set('vendorId')}>
            <option value="">{m.none}</option>
            {vendors.data
              ?.filter((v) => v.active)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} — {m.trades[v.trade]}
                </option>
              ))}
          </select>
        </Field>
      </div>
      <div className="row">
        <Field label={m.scheduledOn} hint={t.common.optional} error={errors.scheduledOn}>
          <input type="date" value={values.scheduledOn} onChange={set('scheduledOn')} />
        </Field>
        <Field label={m.estimate} hint={t.common.optional} error={errors.estimate || undefined}>
          <input inputMode="decimal" value={values.estimate} onChange={set('estimate')} placeholder="0,00 $" />
        </Field>
      </div>
      {create.isError && <Alert>{errorMessage(create.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={create.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
